import { ACCUEIL, resolveLoginRedirect } from './login-redirect.js'

/**
 * La navigation vers un écran de connexion, la page demandée en `?redirect=`.
 *
 * Utilisée par les gardes (back-office ET portails) quand une page exige une
 * session absente. Sans la page, l'ouvrir depuis un favori ou un e-mail la
 * perdait : retour au tableau de bord après connexion.
 *
 * La racine `/` ne part PAS en `redirect` : il n'y a rien à reprendre, et la
 * connexion du back-office lit tout `?redirect=` comme « votre session s'est
 * fermée » — un bandeau qu'on servirait sinon à quiconque tape l'adresse du
 * site.
 *
 * @param {string} loginPath
 * @param {string|undefined} fullPath la page demandée (`to.fullPath`)
 * @returns {string|{path: string, query: {redirect: string}}}
 */
export function versConnexion(loginPath, fullPath) {
  if (typeof fullPath !== 'string' || fullPath === '' || fullPath === '/') return loginPath
  return { path: loginPath, query: { redirect: fullPath } }
}

/**
 * La page à rouvrir après une reconnexion, lue dans `?redirect=`.
 *
 * Quatre écrans de connexion posaient la même question et n'y répondaient pas
 * pareil : le back-office reprenait la page quittée, les trois portails
 * l'ignoraient et ramenaient tout le monde sur leur tableau de bord. La règle
 * vit désormais ici, une fois.
 *
 * Seul un chemin INTERNE est accepté : `//ailleurs.example` est une URL absolue
 * pour le navigateur, et un `redirect` fabriqué enverrait la victime hors de
 * l'application juste après sa connexion. L'écran de connexion lui-même est
 * refusé : il bouclerait.
 *
 * @param {unknown} cible valeur brute de `?redirect=`
 * @param {object} [options]
 * @param {string|null} [options.defaut] où aller si `cible` est inutilisable
 * @param {string|null} [options.prefixe] n'accepter que les pages sous ce
 *   chemin. Un portail ne reprend QUE ses propres pages : un
 *   `?redirect=/quotes/12` sur la connexion formateur ouvrirait un écran de
 *   back-office que le formateur ne peut pas voir.
 * @param {string} [options.loginPath] l'écran de connexion à refuser
 * @returns {string|null}
 */
export function resolveRedirectInterne(
  cible,
  { defaut = '/', prefixe = null, loginPath = '/login' } = {}
) {
  if (typeof cible !== 'string' || cible === '') return defaut
  // Le tri de ce qui est sûr (antislash, caractères de contrôle, `//`, autre
  // origine) est celui de la connexion du back-office : une seule règle.
  const origine = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
  const sure = resolveLoginRedirect(cible, origine)
  if (sure === ACCUEIL && cible !== ACCUEIL) return defaut
  if (sure.startsWith(loginPath)) return defaut
  if (prefixe && sure !== prefixe && !sure.startsWith(`${prefixe}/`)) return defaut
  return sure
}
